'use client'

import { useIntl } from 'react-intl'
import { Dialog, Cell, Text, Button, Divider, Navigation, BottomSheet } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { RefundOfferCurrencyConversionModel } from '@marketplace-web/complaints/complaints-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/domain/currency-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

type Props = {
  refundOffer: CurrencyAmountModel | null
  buyerProtectionFee: CurrencyAmountModel
  currencyConversion: RefundOfferCurrencyConversionModel | null
  isOpenedInDialog?: boolean
  isOpen: boolean
  onClose: () => void
}

const PartialRefundOfferCurrencyConversionDialog = ({
  refundOffer,
  buyerProtectionFee,
  currencyConversion,
  isOpenedInDialog = false,
  isOpen,
  onClose,
}: Props) => {
  const { formatNumber, locale } = useIntl()
  const translate = useTranslate('partial_refund_offer_currency_conversion_dialog')
  const { phones } = useBreakpoint()
  const abTest = useAbTest('dd_order_web_mobile_view_return_flow_convert_dialogs_to_bottom_sheets')
  const isMobileView = abTest?.variant === 'on' && phones && !isOpenedInDialog
  useTrackAbTest(abTest, isOpen && phones && !isOpenedInDialog)

  const renderNavigation = () => {
    return (
      <Navigation
        body={translate('title')}
        right={
          <Button
            inline
            iconName={X24}
            styling={Button.Styling.Flat}
            onClick={onClose}
            aria={{ 'aria-label': translate('a11y.close') }}
          />
        }
      />
    )
  }

  const renderRefundOfferConversionInfo = () => {
    const refundOfferBeforeConversion = currencyConversion?.details.refundOffer

    if (!refundOfferBeforeConversion || !refundOffer) return null

    const priceBeforeConversion = formatCurrencyAmount(refundOfferBeforeConversion, locale)
    const priceAfterConversion = formatCurrencyAmount(refundOffer, locale)

    return (
      <Cell
        title={translate('partial_refund')}
        body={
          <Text
            as="span"
            type={Text.Type.Subtitle}
            html
            text={translate('partial_refund_description', { cost: priceBeforeConversion })}
          />
        }
        suffix={priceAfterConversion}
      />
    )
  }

  const renderBuyerProtectionFeeInfo = () => {
    const price = formatCurrencyAmount(buyerProtectionFee, locale)

    return <Cell title={translate('buyer_protection_fee_refund')} suffix={price} />
  }

  const renderExchangeRate = () => {
    if (!currencyConversion) return null

    const { baseRate, exchangeRate } = currencyConversion.exchange

    const base = formatCurrencyAmount(baseRate, locale)
    const rate = formatCurrencyAmount(exchangeRate, locale)

    return (
      <>
        <Divider />
        <Cell
          body={
            <Text
              as="span"
              text={`${translate('fx_rate.title')} ${translate('fx_rate.rate', { rate, base })}`}
            />
          }
        />
      </>
    )
  }

  const renderPaymentServiceProviderFee = () => {
    if (!currencyConversion) return null

    const { rate, provider } = currencyConversion.markup

    const feePercentage = formatNumber(rate, {
      style: 'percent',
      maximumFractionDigits: 2,
    })

    return (
      <Cell
        body={
          <Text
            as="h3"
            html
            type={Text.Type.Subtitle}
            text={translate('fx_markup_rate', { fee: feePercentage, payment_provider: provider })}
          />
        }
      />
    )
  }

  const renderActions = () => {
    return (
      <Cell
        body={
          <Button
            text={translate('actions.ok')}
            styling={Button.Styling.Filled}
            onClick={onClose}
          />
        }
      />
    )
  }

  const renderContent = () => {
    return (
      <>
        <SeparatedList separator={<Divider />}>
          {renderNavigation()}
          {renderRefundOfferConversionInfo()}
          {renderBuyerProtectionFeeInfo()}
        </SeparatedList>
        {renderExchangeRate()}
        {renderPaymentServiceProviderFee()}
      </>
    )
  }

  if (isMobileView) {
    return (
      <BottomSheet isVisible={isOpen} onClose={onClose}>
        {renderContent()}
      </BottomSheet>
    )
  }

  return (
    <Dialog show={isOpen}>
      {renderContent()}
      {renderActions()}
    </Dialog>
  )
}

export default PartialRefundOfferCurrencyConversionDialog
